/**
 * GTM Sales Insights — Phase 1 + Phase 6
 *
 * KPI cards (scoped to the logged-in gtm_sales user's own accounts):
 *   1. No of Accounts
 *   2. Master Form               (count of mapped Properties)
 *   3. Pending OTA Listing
 *   4. Completed OTA Listing
 *   5. Document Status           (all 5 mandatory checklist items 'Complete')
 *   6. Total Token               (∑ payment_info.token_advance)         [Phase 6]
 *   7. Total Setup Agreed        (∑ payment_info.finalize_setup_token) [Phase 6]
 *   8. Total Pending Payment     (∑ payment_info.pending_payment)      [Phase 6]
 *   9. Documents Pending         (count of accounts with ≥1 mandatory doc missing) [Phase 6]
 *
 * No internal sub-tabs — this view is rendered ONLY when the authenticated
 * user's role === 'gtm_sales'. Backend: GET /api/role-insights/gtm-sales.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, FileText, AlertTriangle, CheckCircle2, ShieldCheck,
  Loader2, RefreshCw, Coins, IndianRupee, Wallet, FileWarning,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';
import DrilldownModal, { ClickableCard } from './DrilldownModal';
import OffboardingInsightsCards from './OffboardingInsightsCards';

// Indian-locale currency formatter — matches LeadModal payment inputs.
const fmtINR = (n) => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `₹${v.toLocaleString('en-IN')}`;
  }
};

const GTMSalesInsights = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [docSummary, setDocSummary] = useState({ late: 0, urgent: 0, critical: 0, total: 0 });
  const [range, setRange] = useState('this_week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const openDrilldown = (card, title) => setDrilldown({ card, title });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const qs = new URLSearchParams(rangeParams(range, customFrom, customTo)).toString();
      const [res, sumRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/gtm-sales?${qs}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/docs-pending-summary?scope=self&${qs}`, { headers }),
      ]);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      if (sumRes.ok) setDocSummary(await sumRes.json());
    } catch (e) {
      setError(e.message || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const cards = data?.cards || {};

  return (
    <div className="space-y-5" data-testid="gtm-sales-insights">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">My Insights</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Personal view — only accounts created by <span className="font-medium text-gray-700">{user?.name || user?.email}</span>.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="gtm-sales-insights-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="gtm-sales-date-filter" />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="gtm-sales-insights-error">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="gtm-sales-cards-grid">
          <ClickableCard
            label="No of Accounts"
            value={cards.no_of_accounts ?? 0}
            Icon={Briefcase}
            accent={{ bg: 'bg-teal-50', fg: 'text-teal-600' }}
            testId="gtm-sales-card-accounts"
            onClick={() => openDrilldown('no_of_accounts', 'No of Accounts')}
          />
          <ClickableCard
            label="Master Form"
            value={cards.master_form_completed ?? 0}
            Icon={FileText}
            accent={{ bg: 'bg-indigo-50', fg: 'text-indigo-600' }}
            testId="gtm-sales-card-master-form"
            onClick={() => openDrilldown('master_form', 'Master Form')}
          />
          <ClickableCard
            label="Pending OTA Listing"
            value={cards.pending_ota_listing ?? 0}
            Icon={AlertTriangle}
            accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }}
            testId="gtm-sales-card-pending-ota"
            onClick={() => openDrilldown('pending_ota', 'Pending OTA Listings')}
          />
          <ClickableCard
            label="Completed OTA Listing"
            value={cards.completed_ota_listing ?? 0}
            Icon={CheckCircle2}
            accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }}
            testId="gtm-sales-card-completed-ota"
            onClick={() => openDrilldown('completed_ota', 'Completed OTA Listings')}
          />
          <ClickableCard
            label="Document Status"
            value={cards.document_status_completed ?? 0}
            Icon={ShieldCheck}
            accent={{ bg: 'bg-violet-50', fg: 'text-violet-600' }}
            testId="gtm-sales-card-doc-status"
            onClick={() => openDrilldown('document_status', 'Document Status')}
          />
          {/* ── Phase 6 — Financial + Documents Pending ────────────────── */}
          <ClickableCard
            label="Total Token"
            value={fmtINR(cards.total_token)}
            Icon={Coins}
            accent={{ bg: 'bg-yellow-50', fg: 'text-yellow-600' }}
            testId="gtm-sales-card-total-token"
            onClick={() => openDrilldown('total_token', 'Total Token')}
          />
          <ClickableCard
            label="Total Setup Agreed"
            value={fmtINR(cards.total_setup_agreed)}
            Icon={IndianRupee}
            accent={{ bg: 'bg-sky-50', fg: 'text-sky-600' }}
            testId="gtm-sales-card-total-setup-agreed"
            onClick={() => openDrilldown('total_setup_agreed', 'Total Setup Agreed')}
          />
          <ClickableCard
            label="Total Pending Payment"
            value={fmtINR(cards.total_pending_payment)}
            Icon={Wallet}
            accent={{ bg: 'bg-rose-50', fg: 'text-rose-600' }}
            testId="gtm-sales-card-total-pending-payment"
            onClick={() => openDrilldown('total_pending_payment', 'Total Pending Payment')}
          />
          <div
            className="relative group"
            data-testid="gtm-sales-card-documents-pending-wrap"
          >
            <ClickableCard
              label="Documents Pending"
              value={cards.documents_pending ?? 0}
              Icon={FileWarning}
              accent={{ bg: 'bg-orange-50', fg: 'text-orange-600' }}
              testId="gtm-sales-card-documents-pending"
              onClick={() => openDrilldown('documents_pending', 'Documents Pending')}
            />
            <div
              className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-56 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white rounded-lg shadow-xl px-3 py-2 text-xs"
              data-testid="gtm-sales-doc-pending-tooltip"
            >
              <div className="flex items-center justify-between py-0.5">
                <span className="text-gray-300">Late</span>
                <span className="font-bold text-amber-300" data-testid="gtm-sales-doc-late">{docSummary.late}</span>
              </div>
              <div className="flex items-center justify-between py-0.5">
                <span className="text-gray-300">Urgent</span>
                <span className="font-bold text-orange-300" data-testid="gtm-sales-doc-urgent">{docSummary.urgent}</span>
              </div>
              <div className="flex items-center justify-between py-0.5">
                <span className="text-gray-300">Critical</span>
                <span className="font-bold text-rose-300" data-testid="gtm-sales-doc-critical">{docSummary.critical}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {drilldown && (
        <DrilldownModal
          title={drilldown.title}
          card={drilldown.card}
          scope="self"
          rangeParams={rangeParams(range, customFrom, customTo)}
          onClose={() => setDrilldown(null)}
          rowAction={drilldown.card === 'documents_pending' ? {
            label: 'Open',
            onClick: (row) => { setDrilldown(null); navigate(`/leads/${row.lead_id}`); },
          } : null}
        />
      )}

      {/* Phase 16 — Offboarding cards (owner-scoped automatically by backend). */}
      <OffboardingInsightsCards />
    </div>
  );
};

export default GTMSalesInsights;
